/**
 * ProfileHeader Component
 *
 * Header section for profile page with avatar, name, badge, rating, and bio
 */

'use client';

import { useState } from 'react';
import { useTranslations } from 'next-intl';
import { Avatar } from '@/src/shared/ui/avatars/Avatar';
import { Typography } from '@/src/shared/ui/typography/Typography';
import { Icon } from '@/src/shared/ui/icons/Icon';
import { RatingStars } from '@/src/shared/ui';
import type { ProfileViewType } from '../types';
import { Breadcrumb } from '@/src/shared/ui/navigation';
import type { BreadcrumbItem } from '@/src/shared/ui/navigation/Breadcrumb';
import { PrimaryButton } from '@/src/shared/ui/button/PrimaryButton';

export interface ProfileHeaderProps {
  name: string;
  avatarUrl: string | null;
  badge?: string | null; // e.g., "Top Service Provider"
  city: string | null;
  postalCode?: string | null;
  rating?: number | null;
  reviewsCount?: number;
  tasksCompleted?: number;
  viewType: ProfileViewType;
  isOnline?: boolean;
  responseTime?: string | null; // e.g., "1 hour"
  bio?: string | null;
  socialLinks?: {
    instagram?: string;
    linkedin?: string;
  };
  isCompact?: boolean; // Compact mode for tabs (smaller avatar, hidden bio)
  activeTab?: 'overview' | 'orders' | 'messages' | 'settings';
  onTabChange?: (tab: 'overview' | 'orders' | 'messages' | 'settings') => void;
  onReportClick?: () => void; // Handler for Report button (public profile)
  onMessageClick?: () => void; // Handler for Message button (public profile)
  breadcrumbItems?: BreadcrumbItem[]; // Trail above the header; hidden when empty
}

export function ProfileHeader({
  name,
  avatarUrl,
  badge,
  city,
  postalCode,
  rating,
  reviewsCount = 0,
  tasksCompleted,
  viewType,
  isOnline,
  responseTime,
  bio,
  socialLinks,
  isCompact = false,
  activeTab = 'overview',
  onTabChange,
  onReportClick,
  onMessageClick,
  breadcrumbItems = [],
}: ProfileHeaderProps) {
  const t = useTranslations('profile');
  const [isExpanded, setIsExpanded] = useState(false);

  const isContractor = viewType === 'contractor-own' || viewType === 'contractor-public';
  const isOwnProfile = viewType === 'contractor-own' || viewType === 'client-own';
  const location = city ? `${city}${postalCode ? ` - ${postalCode}` : ''}` : null;

  // Avatar size based on compact mode
  const avatarSize = isCompact ? 'xs' : 'xl';

  return (
    <div className="relative container-auto">
      {/* Compact Header - Avatar + "Profile" text */}

      {/* Navigation - Tabs for own profile, Report button for public profile */}
      <div className={`pb-3 mb-4 flex flex-row items-end justify-between transition-all duration-300 ${
        isCompact ? 'h-auto' : 'h-[103px]'
      }`}>
        {/* Breadcrumb - Hidden in compact mode */}
        {!isCompact && breadcrumbItems.length > 0 && (
          <div className="transition-opacity duration-300">
            <Breadcrumb items={breadcrumbItems} textVariant="h3" />
          </div>
        )}

        {/* Compact mode - Avatar + Profile text (own profile only) */}
        {isCompact && isOwnProfile && (
          <button
            onClick={() => onTabChange?.('overview')}
            className="flex items-center gap-2.5 mb-4 animate-fadeIn hover:opacity-80 transition-opacity"
          >
            <div className="relative">
              <Avatar
                src={avatarUrl || undefined}
                alt={name}
                size="xs"
                fallback={name.charAt(0).toUpperCase()}
                className="transition-all duration-300 ease-in-out"
              />
            </div>
            <Typography
              variant="h3"
              weight="semibold"
              className="text-text-primary animate-fadeIn"
            >
              {t('profile')}
            </Typography>
          </button>
        )}

        {/* Own Profile - Tabs (Orders, Messages, Settings) */}
        {isOwnProfile && onTabChange && (
          <div className="flex flex-row items-center gap-6">
            <button
              onClick={() => onTabChange('orders')}
              className={`flex flex-row items-center gap-2 transition-colors ${
                activeTab === 'orders'
                  ? 'text-accent-primary'
                  : 'text-text-primary hover:text-accent-primary'
              }`}
            >
              <Icon name="orders" size="md" />
              <Typography variant="h3" weight="bold">
                {t('tabs.orders')}
              </Typography>
            </button>

            <button
              onClick={() => onTabChange('messages')}
              className={`flex flex-row items-center gap-2 transition-colors ${
                activeTab === 'messages'
                  ? 'text-accent-primary'
                  : 'text-text-primary hover:text-accent-primary'
              }`}
            >
              <Icon name="email" size="md" />
              <Typography variant="h3" weight="bold">
                {t('tabs.messages')}
              </Typography>
            </button>

            <button
              onClick={() => onTabChange('settings')}
              className={`flex flex-row items-center gap-2 transition-colors ${
                activeTab === 'settings'
                  ? 'text-accent-primary'
                  : 'text-text-primary hover:text-accent-primary'
              }`}
            >
              <Icon name="settings" size="md" />
              <Typography variant="h3" weight="bold">
                {t('tabs.settings')}
              </Typography>
            </button>
          </div>
        )}

        {/* Public Profile - Report button */}
        {!isOwnProfile && onReportClick && (
          <div className="flex flex-row items-center gap-6">
            <button
              onClick={onReportClick}
              className="flex flex-row items-center gap-2 text-text-primary hover:text-accent-primary transition-colors"
            >
              <Icon name="report" size="md" />
              <Typography variant="h3" weight="bold">
                {t('report')}
              </Typography>
            </button>
          </div>
        )}
      </div>

      {/* Full Header Content - Hidden in compact mode */}
      <div className={`flex flex-col md:flex-row items-start gap-6 transition-all duration-300 ${
        isCompact ? 'opacity-0 h-0 overflow-hidden' : 'opacity-100'
      }`}>
        {/* Avatar Section */}
        <div className="flex flex-col items-center transition-all duration-300 ease-in-out">
          <div className="relative mb-3">
            <Avatar
              src={avatarUrl || undefined}
              alt={name}
              size={avatarSize}
              fallback={name.charAt(0).toUpperCase()}
              className="transition-all duration-300 ease-in-out"
            />
            {isContractor && isOnline !== undefined && (
              <span
                className={`absolute bottom-1 right-1 w-4 h-4 rounded-full border-2 border-white transition-all duration-300 ${
                  isOnline ? 'bg-feedback-success' : 'bg-text-tertiary'
                }`}
              />
            )}
          </div>

          {/* Online Status */}
          {isContractor && isOnline !== undefined && (
            <Typography
              variant="body"
              weight="semibold"
              className={`transition-opacity duration-300 ${isOnline ? 'text-feedback-success' : 'text-text-tertiary'}`}
            >
              {isOnline ? t('online') : t('offline')}
            </Typography>
          )}

          {/* Response Time */}
          {responseTime && (
            <Typography variant="bodySm" color="secondary" className="mt-1 transition-opacity duration-300">
              {t('respondsIn', { time: responseTime })}
            </Typography>
          )}

          {/* Message Button - Public Profile Only */}
          {!isOwnProfile && onMessageClick && (
            <PrimaryButton
              onClick={onMessageClick}
              className="mt-4 w-full"
            >
              {t('actions.message')}
            </PrimaryButton>
          )}
        </div>

        {/* Info Section */}
        <div className="flex-1">
          {/* Name and Badge */}
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2 mb-2 transition-opacity duration-300">
            <div className="flex items-center gap-2">
              <Typography variant="h2" weight="bold">
                {name}
              </Typography>
              {badge && (
                <span className="px-2 py-1 bg-feedback-success/10 text-feedback-success text-sm font-semibold rounded transition-opacity duration-300">
                  {badge}
                </span>
              )}
            </div>

            {/* Social Links */}
            {socialLinks && (
              <div className="flex items-center gap-4 transition-opacity duration-300">
                {socialLinks.instagram && (
                  <a
                    href={socialLinks.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-feedback-success hover:underline font-medium"
                  >
                    {t('instagram')}
                  </a>
                )}
                {socialLinks.linkedin && (
                  <a
                    href={socialLinks.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-feedback-success hover:underline font-medium"
                  >
                    {t('linkedin')}
                  </a>
                )}
              </div>
            )}
          </div>

          {/* Location */}
          {location && (
            <Typography variant="body" color="primary" className="mb-2 transition-opacity duration-300">
              {location}
            </Typography>
          )}

          {/* Rating */}
          {rating !== undefined && rating !== null && (
            <div className="transition-opacity duration-300">
              <RatingStars
                rating={rating}
                reviewCount={reviewsCount}
                variant="inline"
                size="lg"
              />
            </div>
          )}

          {/* Tasks Completed */}
          {tasksCompleted !== undefined && (
            <Typography variant="body" weight="semibold" className="mb-4 transition-opacity duration-300">
              {t('tasksCompleted', { count: tasksCompleted })}
            </Typography>
          )}

          {/* Bio - Already hidden in compact mode */}
          {bio && (
            <div className="mt-4 transition-opacity duration-300">
              <Typography variant="body" color="primary" className="whitespace-pre-wrap">
                {isExpanded ? bio : `${bio.slice(0, 200)}${bio.length > 200 ? '...' : ''}`}
              </Typography>
              {bio.length > 200 && (
                <button
                  onClick={() => setIsExpanded(!isExpanded)}
                  className="text-feedback-success font-semibold mt-2 hover:underline"
                >
                  {isExpanded ? t('readLess') : t('readMore')}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
